import {
  CheckCircleIcon,
  ClockIcon,
  TruckIcon,
  CreditCardIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  PauseIcon,
  CheckIcon
} from '@heroicons/react/24/outline';

// Status color mapping - converted to Tailwind classes
export const getStatusColor = (status) => {
  const statusColors = {
    pending: 'text-amber-500 bg-amber-50 border-amber-200',
    confirmed: 'text-blue-500 bg-blue-50 border-blue-200',
    quotation_sent: 'text-purple-500 bg-purple-50 border-purple-200',
    awaiting_payment: 'text-orange-500 bg-orange-50 border-orange-200',
    processing: 'text-indigo-500 bg-indigo-50 border-indigo-200',
    shipped: 'text-cyan-500 bg-cyan-50 border-cyan-200',
    delivered: 'text-green-500 bg-green-50 border-green-200',
    completed: 'text-emerald-500 bg-emerald-50 border-emerald-200',
    on_hold: 'text-gray-500 bg-gray-50 border-gray-200',
    cancelled: 'text-red-500 bg-red-50 border-red-200'
  };
  return statusColors[status] || 'text-gray-500 bg-gray-50 border-gray-200';
};

// Status color mapping for backgrounds only
export const getStatusBgColor = (status) => {
  const statusColors = {
    pending: 'bg-amber-500',
    confirmed: 'bg-blue-500',
    quotation_sent: 'bg-purple-500',
    awaiting_payment: 'bg-orange-500',
    processing: 'bg-indigo-500',
    shipped: 'bg-cyan-500',
    delivered: 'bg-green-500',
    completed: 'bg-emerald-500',
    on_hold: 'bg-gray-500',
    cancelled: 'bg-red-500'
  };
  return statusColors[status] || 'bg-gray-500';
};

// Status color mapping for text only
export const getStatusTextColor = (status) => {
  const statusColors = {
    pending: 'text-amber-600',
    confirmed: 'text-blue-600',
    quotation_sent: 'text-purple-600',
    awaiting_payment: 'text-orange-600',
    processing: 'text-indigo-600',
    shipped: 'text-cyan-600',
    delivered: 'text-green-600',
    completed: 'text-emerald-600',
    on_hold: 'text-gray-600',
    cancelled: 'text-red-600'
  };
  return statusColors[status] || 'text-gray-600';
};

// Priority color mapping - converted to Tailwind classes
export const getPriorityColor = (priority) => {
  const priorityColors = {
    low: 'text-green-500 bg-green-50 border-green-200',
    medium: 'text-amber-500 bg-amber-50 border-amber-200',
    high: 'text-red-500 bg-red-50 border-red-200',
    urgent: 'text-pink-500 bg-pink-50 border-pink-200'
  };
  return priorityColors[priority] || 'text-gray-500 bg-gray-50 border-gray-200';
};

// Priority color mapping for backgrounds only
export const getPriorityBgColor = (priority) => {
  const priorityColors = {
    low: 'bg-green-500',
    medium: 'bg-amber-500',
    high: 'bg-red-500',
    urgent: 'bg-pink-500'
  };
  return priorityColors[priority] || 'bg-gray-500';
};

// Priority color mapping for text only
export const getPriorityTextColor = (priority) => {
  const priorityColors = {
    low: 'text-green-600',
    medium: 'text-amber-600',
    high: 'text-red-600',
    urgent: 'text-pink-600'
  };
  return priorityColors[priority] || 'text-gray-600';
};

// Status icon mapping
export const getStatusIcon = (status) => {
  const statusIcons = {
    pending: ClockIcon,
    confirmed: CheckCircleIcon,
    quotation_sent: CreditCardIcon,
    awaiting_payment: CreditCardIcon,
    processing: ExclamationTriangleIcon,
    shipped: TruckIcon,
    delivered: CheckIcon,
    completed: CheckCircleIcon,
    on_hold: PauseIcon,
    cancelled: XCircleIcon
  };
  return statusIcons[status] || ClockIcon;
};

// Tailwind CSS classes for different UI components
export const getCardClasses = (variant = 'default') => {
  const variants = {
    default: 'bg-white rounded-lg shadow-sm border border-gray-200 p-6',
    elevated: 'bg-white rounded-lg shadow-md border border-gray-200 p-6',
    outlined: 'bg-white rounded-lg border-2 border-gray-300 p-6',
    flat: 'bg-gray-50 rounded-lg p-6'
  };
  return variants[variant] || variants.default;
};

export const getButtonClasses = (variant = 'primary', size = 'md') => {
  const baseClasses = 'inline-flex items-center justify-center font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors duration-200';
  
  const variants = {
    primary: 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500',
    secondary: 'bg-gray-600 text-white hover:bg-gray-700 focus:ring-gray-500',
    success: 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500',
    danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
    warning: 'bg-amber-600 text-white hover:bg-amber-700 focus:ring-amber-500',
    outline: 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:ring-blue-500',
    ghost: 'text-gray-700 hover:bg-gray-100 focus:ring-gray-500'
  };
  
  const sizes = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base',
    xl: 'px-8 py-4 text-lg'
  };
  
  return `${baseClasses} ${variants[variant] || variants.primary} ${sizes[size] || sizes.md}`;
};

export const getInputClasses = (variant = 'default', hasError = false) => {
  const baseClasses = 'block w-full rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-0 transition-colors duration-200';
  
  const variants = {
    default: 'border-gray-300 focus:border-blue-500 focus:ring-blue-500',
    filled: 'bg-gray-50 border-gray-300 focus:bg-white focus:border-blue-500 focus:ring-blue-500',
    outlined: 'border-2 border-gray-300 focus:border-blue-500 focus:ring-blue-500'
  };
  
  const errorClasses = hasError ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : '';
  
  return `${baseClasses} ${variants[variant] || variants.default} ${errorClasses}`;
};

export const getChipClasses = (variant = 'default', size = 'md') => {
  const baseClasses = 'inline-flex items-center font-medium rounded-full';
  
  const variants = {
    default: 'bg-gray-100 text-gray-800',
    primary: 'bg-blue-100 text-blue-800',
    success: 'bg-green-100 text-green-800',
    warning: 'bg-amber-100 text-amber-800',
    danger: 'bg-red-100 text-red-800',
    info: 'bg-cyan-100 text-cyan-800'
  };
  
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-0.5 text-sm',
    lg: 'px-3 py-1 text-base'
  };
  
  return `${baseClasses} ${variants[variant] || variants.default} ${sizes[size] || sizes.md}`;
};

export const getTableClasses = () => {
  return {
    container: 'overflow-x-auto shadow ring-1 ring-black ring-opacity-5 rounded-lg',
    table: 'min-w-full divide-y divide-gray-300',
    thead: 'bg-gray-50',
    th: 'px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider',
    tbody: 'bg-white divide-y divide-gray-200',
    tr: 'hover:bg-gray-50 transition-colors duration-150',
    td: 'px-6 py-4 whitespace-nowrap text-sm text-gray-900'
  };
};

export const getModalClasses = () => {
  return {
    backdrop: 'fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity z-40',
    container: 'fixed inset-0 z-50 overflow-y-auto',
    wrapper: 'flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0',
    modal: 'inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full',
    header: 'bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4',
    body: 'bg-white px-4 pb-4 sm:p-6',
    footer: 'bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse'
  };
};

export const getAlertClasses = (variant = 'info') => {
  const baseClasses = 'rounded-md p-4 border';
  
  const variants = {
    success: 'bg-green-50 border-green-200 text-green-800',
    error: 'bg-red-50 border-red-200 text-red-800',
    warning: 'bg-amber-50 border-amber-200 text-amber-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800'
  };
  
  return `${baseClasses} ${variants[variant] || variants.info}`;
};

export const getProgressBarClasses = () => {
  return {
    container: 'w-full bg-gray-200 rounded-full h-2',
    bar: 'bg-blue-600 h-2 rounded-full transition-all duration-300 ease-in-out'
  };
};

export const getBadgeClasses = (variant = 'default', size = 'md') => {
  const baseClasses = 'inline-flex items-center font-medium rounded-full';
  
  const variants = {
    default: 'bg-gray-100 text-gray-800',
    primary: 'bg-blue-100 text-blue-800',
    success: 'bg-green-100 text-green-800',
    warning: 'bg-amber-100 text-amber-800',
    danger: 'bg-red-100 text-red-800'
  };
  
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-0.5 text-sm',
    lg: 'px-3 py-1 text-base'
  };
  
  return `${baseClasses} ${variants[variant] || variants.default} ${sizes[size] || sizes.md}`;
};

export const getSkeletonClasses = () => {
  return {
    base: 'animate-pulse bg-gray-200 rounded',
    text: 'h-4 bg-gray-200 rounded animate-pulse',
    avatar: 'w-10 h-10 bg-gray-200 rounded-full animate-pulse',
    button: 'h-10 bg-gray-200 rounded animate-pulse',
    card: 'p-4 bg-gray-200 rounded-lg animate-pulse'
  };
};

export const getSpinnerClasses = (size = 'md') => {
  const baseClasses = 'animate-spin rounded-full border-2 border-gray-300 border-t-blue-600';
  
  const sizes = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
    xl: 'w-12 h-12'
  };
  
  return `${baseClasses} ${sizes[size] || sizes.md}`;
};

// Format currency
export const formatCurrency = (amount, currency = 'INR') => {
  if (typeof amount !== 'number') {
    amount = parseFloat(amount) || 0;
  }
  
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};


// Format date
export const formatDate = (date, format = 'date') => {
  if (!date) return '';
  
  const dateObj = new Date(date);
  
  if (format === 'time') {
    return dateObj.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  }
  
  if (format === 'datetime') {
    return dateObj.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
  
  return dateObj.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

// Calculate order progress based on status
export const calculateOrderProgress = (order) => {
  const progressMap = {
    pending: 10,
    confirmed: 25,
    quotation_sent: 35,
    awaiting_payment: 45,
    processing: 60,
    shipped: 80,
    delivered: 95,
    completed: 100,
    on_hold: 30,
    cancelled: 0
  };
  
  return progressMap[order.status] || 0;
};

// Generate order status timeline
export const generateOrderTimeline = (order) => {
  const timeline = [];
  
  // Add order creation
  timeline.push({
    timestamp: order.orderDate,
    title: 'Order Created',
    description: `Order ${order.id} was created`,
    type: 'status',
    user: 'System'
  });
  
  // Add status changes based on current status
  const statusFlow = [
    'pending',
    'confirmed', 
    'quotation_sent',
    'awaiting_payment',
    'processing',
    'shipped',
    'delivered',
    'completed'
  ];
  
  const currentIndex = statusFlow.indexOf(order.status);
  
  for (let i = 1; i <= currentIndex; i++) {
    timeline.push({
      timestamp: new Date(Date.now() - (currentIndex - i) * 24 * 60 * 60 * 1000),
      title: `Status Updated to ${statusFlow[i].replace('_', ' ').toUpperCase()}`,
      description: `Order status changed to ${statusFlow[i]}`,
      type: 'status',
      user: 'Admin'
    });
  }
  
  // Add communications if available
  if (order.communications) {
    order.communications.forEach(comm => {
      timeline.push({
        timestamp: comm.timestamp,
        title: comm.subject || `${comm.type.toUpperCase()} Communication`,
        description: comm.message,
        type: comm.type,
        user: comm.user
      });
    });
  }
  
  return timeline.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
};

// Filter orders by various criteria
export const filterOrders = (orders, filters) => {
  let filtered = [...orders];
  
  // Search filter
  if (filters.searchTerm) {
    const searchLower = filters.searchTerm.toLowerCase();
    filtered = filtered.filter(order => 
      order.id.toLowerCase().includes(searchLower) ||
      order.customerName.toLowerCase().includes(searchLower) ||
      order.customerEmail.toLowerCase().includes(searchLower) ||
      order.supplierName.toLowerCase().includes(searchLower) ||
      order.supplierEmail.toLowerCase().includes(searchLower)
    );
  }
  
  // Status filter
  if (filters.statusFilter && filters.statusFilter !== 'all') {
    filtered = filtered.filter(order => order.status === filters.statusFilter);
  }
  
  // Priority filter
  if (filters.priorityFilter && filters.priorityFilter !== 'all') {
    filtered = filtered.filter(order => order.priority === filters.priorityFilter);
  }
  
  // Date range filter
  if (filters.dateFrom) {
    filtered = filtered.filter(order => 
      new Date(order.orderDate) >= new Date(filters.dateFrom)
    );
  }
  
  if (filters.dateTo) {
    filtered = filtered.filter(order => 
      new Date(order.orderDate) <= new Date(filters.dateTo)
    );
  }
  
  // Amount range filter
  if (filters.amountFrom) {
    filtered = filtered.filter(order => order.totalAmount >= filters.amountFrom);
  }
  
  if (filters.amountTo) {
    filtered = filtered.filter(order => order.totalAmount <= filters.amountTo);
  }
  
  return filtered;
};

// Sort orders by various criteria
export const sortOrders = (orders, sortBy, sortOrder = 'desc') => {
  const sorted = [...orders];
  
  sorted.sort((a, b) => {
    let aValue, bValue;
    
    switch (sortBy) {
      case 'date':
        aValue = new Date(a.orderDate);
        bValue = new Date(b.orderDate);
        break;
      case 'amount':
        aValue = a.totalAmount;
        bValue = b.totalAmount;
        break;
      case 'status':
        aValue = a.status;
        bValue = b.status;
        break;
      case 'customer':
        aValue = a.customerName.toLowerCase();
        bValue = b.customerName.toLowerCase();
        break;
      case 'supplier':
        aValue = a.supplierName.toLowerCase();
        bValue = b.supplierName.toLowerCase();
        break;
      case 'priority':
        const priorityOrder = { low: 1, medium: 2, high: 3, urgent: 4 };
        aValue = priorityOrder[a.priority] || 0;
        bValue = priorityOrder[b.priority] || 0;
        break;
      case 'lastInteraction':
        aValue = new Date(a.lastInteraction);
        bValue = new Date(b.lastInteraction);
        break;
      case 'id':
        aValue = a.id;
        bValue = b.id;
        break;
      default:
        aValue = a.id;
        bValue = b.id;
    }
    
    if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });
  
  return sorted;
};

// Calculate dashboard metrics
export const calculateMetrics = (orders) => {
  const metrics = {
    totalOrders: orders.length,
    activeOrders: 0,
    totalRevenue: 0,
    avgOrderValue: 0,
    highPriorityOrders: 0,
    recentInteractions: 0,
    statusDistribution: {},
    priorityDistribution: {},
    revenueByStatus: {},
    ordersToday: 0,
    ordersThisWeek: 0,
    ordersThisMonth: 0,
    completedToday: 0
  };
  
  const today = new Date();
  const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
  const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
  const dayAgo = new Date(today.getTime() - 24 * 60 * 60 * 1000);
  
  orders.forEach(order => {
    const orderDate = new Date(order.orderDate);
    const lastInteraction = new Date(order.lastInteraction);
    
    // Active orders (not completed, cancelled, or delivered)
    if (!['completed', 'cancelled', 'delivered'].includes(order.status)) {
      metrics.activeOrders++;
    }
    
    // Total revenue
    metrics.totalRevenue += order.totalAmount || 0;
    
    // High priority orders
    if (['high', 'urgent'].includes(order.priority)) {
      metrics.highPriorityOrders++;
    }
    
    // Recent interactions (last 24 hours)
    if (lastInteraction >= dayAgo) {
      metrics.recentInteractions++;
    }
    
    // Completed today
    if (['completed', 'delivered'].includes(order.status) && 
        orderDate.toDateString() === today.toDateString()) {
      metrics.completedToday++;
    }
    
    // Status distribution
    metrics.statusDistribution[order.status] = 
      (metrics.statusDistribution[order.status] || 0) + 1;
    
    // Priority distribution
    metrics.priorityDistribution[order.priority] = 
      (metrics.priorityDistribution[order.priority] || 0) + 1;
    
    // Revenue by status
    metrics.revenueByStatus[order.status] = 
      (metrics.revenueByStatus[order.status] || 0) + (order.totalAmount || 0);
    
    // Time-based metrics
    if (orderDate.toDateString() === today.toDateString()) {
      metrics.ordersToday++;
    }
    
    if (orderDate >= weekAgo) {
      metrics.ordersThisWeek++;
    }
    
    if (orderDate >= monthAgo) {
      metrics.ordersThisMonth++;
    }
  });
  
  // Calculate average order value
  metrics.avgOrderValue = metrics.totalOrders > 0 
    ? metrics.totalRevenue / metrics.totalOrders 
    : 0;
  
  return metrics;
};

// Export orders to various formats
export const exportOrdersToCSV = (orders) => {
  const headers = [
    'Order ID',
    'Customer Name',
    'Customer Email',
    'Supplier Name',
    'Supplier Email',
    'Status',
    'Priority',
    'Order Date',
    'Total Amount',
    'Items Count',
    'Last Interaction'
  ];
  
  const csvContent = [
    headers.join(','),
    ...orders.map(order => [
      order.id,
      `"${order.customerName}"`,
      order.customerEmail,
      `"${order.supplierName}"`,
      order.supplierEmail,
      order.status,
      order.priority,
      formatDate(order.orderDate),
      order.totalAmount,
      order.items?.length || 0,
      formatDate(order.lastInteraction, 'datetime')
    ].join(','))
  ].join('\n');
  
  return csvContent;
};

// Download file helper
export const downloadFile = (content, filename, contentType = 'text/csv') => {
  const blob = new Blob([content], { type: contentType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// Validate email format
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Generate order summary
export const generateOrderSummary = (order) => {
  return {
    id: order.id,
    customer: order.customerName,
    supplier: order.supplierName,
    status: order.status,
    priority: order.priority,
    amount: formatCurrency(order.totalAmount),
    date: formatDate(order.orderDate),
    progress: calculateOrderProgress(order),
    itemCount: order.items?.length || 0,
    lastUpdate: formatDate(order.lastInteraction, 'datetime')
  };
};

// Check if order needs attention
export const orderNeedsAttention = (order) => {
  const now = new Date();
  const lastInteraction = new Date(order.lastInteraction);
  const hoursSinceLastInteraction = (now - lastInteraction) / (1000 * 60 * 60);
  
  // High priority orders not updated in 2 hours
  if (order.priority === 'urgent' && hoursSinceLastInteraction > 2) {
    return { level: 'urgent', reason: 'Urgent order not updated in 2+ hours' };
  }
  
  // High priority orders not updated in 4 hours
  if (order.priority === 'high' && hoursSinceLastInteraction > 4) {
    return { level: 'high', reason: 'High priority order not updated in 4+ hours' };
  }
  
  // Orders stuck in pending status for more than 24 hours
  if (order.status === 'pending' && hoursSinceLastInteraction > 24) {
    return { level: 'medium', reason: 'Order pending for more than 24 hours' };
  }
  
  // Orders awaiting payment for more than 48 hours
  if (order.status === 'awaiting_payment' && hoursSinceLastInteraction > 48) {
    return { level: 'medium', reason: 'Payment pending for more than 48 hours' };
  }
  
  return null;
};

// Get orders by tab category
export const getOrdersByTab = (orders, tabIndex) => {
  switch (tabIndex) {
    case 0: // All Orders
      return orders;
    case 1: // Pending Orders
      return orders.filter(order => 
        ['pending', 'confirmed'].includes(order.status)
      );
    case 2: // Active Orders
      return orders.filter(order => 
        ['processing', 'shipped'].includes(order.status)
      );
    case 3: // Completed Orders
      return orders.filter(order => 
        ['delivered', 'completed'].includes(order.status)
      );
    case 4: // Needs Attention
      return orders.filter(order => 
        orderNeedsAttention(order) !== null
      );
    default:
      return orders;
  }
};

// Format phone number
export const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, '');
  
  // Format as (XXX) XXX-XXXX for US numbers
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  
  // Return original if not a standard format
  return phone;
};

// Debounce function for search
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Local storage helpers
export const saveToLocalStorage = (key, data) => {
  try {
    localStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.error('Error saving to localStorage:', error);
  }
};

export const loadFromLocalStorage = (key, defaultValue = null) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : defaultValue;
  } catch (error) {
    console.error('Error loading from localStorage:', error);
    return defaultValue;
  }
};

// Generate mock data for development
export const generateMockOrders = (count = 50) => {
  const statuses = ['pending', 'confirmed', 'quotation_sent', 'awaiting_payment', 'processing', 'shipped', 'delivered', 'completed', 'on_hold', 'cancelled'];
  const priorities = ['low', 'medium', 'high', 'urgent'];
  const customers = ['John Doe', 'Jane Smith', 'Bob Johnson', 'Alice Brown', 'Charlie Wilson', 'Diana Prince', 'Edward Norton', 'Fiona Apple', 'George Lucas', 'Helen Troy'];
  const suppliers = ['ABC Supplies', 'XYZ Materials', 'Best Builders', 'Quality Goods', 'Prime Vendors', 'Elite Distributors', 'Superior Materials', 'Top Tier Supplies', 'PremiumPartners', 'Excellence Corp'];
  const customerEmails = ['john.doe@email.com', 'jane.smith@email.com', 'bob.johnson@email.com', 'alice.brown@email.com', 'charlie.wilson@email.com'];
  const supplierEmails = ['sales@abcsupplies.com', 'orders@xyzmaterials.com', 'contact@bestbuilders.com', 'info@qualitygoods.com', 'support@primevendors.com'];
  
  return Array.from({ length: count }, (_, index) => ({
    id: `ORD-${String(index + 1).padStart(4, '0')}`,
    customerName: customers[Math.floor(Math.random() * customers.length)],
    customerEmail: customerEmails[Math.floor(Math.random() * customerEmails.length)],
    customerPhone: `+1-${Math.floor(Math.random() * 900) + 100}-${Math.floor(Math.random() * 900) + 100}-${Math.floor(Math.random() * 9000) + 1000}`,
    customerAddress: `${Math.floor(Math.random() * 9999) + 1} Main St, City, State ${Math.floor(Math.random() * 90000) + 10000}`,
    supplierName: suppliers[Math.floor(Math.random() * suppliers.length)],
    supplierEmail: supplierEmails[Math.floor(Math.random() * supplierEmails.length)],
    supplierPhone: `+1-${Math.floor(Math.random() * 900) + 100}-${Math.floor(Math.random() * 900) + 100}-${Math.floor(Math.random() * 9000) + 1000}`,
    supplierAddress: `${Math.floor(Math.random() * 9999) + 1} Business Ave, Industrial City, State ${Math.floor(Math.random() * 90000) + 10000}`,
    status: statuses[Math.floor(Math.random() * statuses.length)],
    priority: priorities[Math.floor(Math.random() * priorities.length)],
    orderDate: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000),
    totalAmount: Math.floor(Math.random() * 10000) + 500,
    lastInteraction: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000),
    expectedDelivery: new Date(Date.now() + Math.random() * 14 * 24 * 60 * 60 * 1000),
    specialInstructions: Math.random() > 0.7 ? 'Handle with care, fragile items included' : null,
    deliveryNotes: Math.random() > 0.8 ? 'Deliver to loading dock at rear of building' : null,
    paymentTerms: ['Net 30', 'Net 15', 'COD', 'Prepaid'][Math.floor(Math.random() * 4)],
    shippingMethod: ['Standard Shipping', 'Express Delivery', 'Overnight', 'Ground'][Math.floor(Math.random() * 4)],
    items: Array.from({ length: Math.floor(Math.random() * 5) + 1 }, (_, i) => ({
      id: `item-${i + 1}`,
      name: `Product ${i + 1}`,
      description: `High quality product ${i + 1} with excellent specifications`,
      quantity: Math.floor(Math.random() * 10) + 1,
      price: Math.floor(Math.random() * 500) + 50
    })),
    communications: Array.from({ length: Math.floor(Math.random() * 3) }, (_, i) => ({
      type: ['email', 'phone', 'chat'][Math.floor(Math.random() * 3)],
      timestamp: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000),
      subject: `Communication ${i + 1}`,
      message: `Sample communication message ${i + 1} regarding order details and requirements`,
      user: ['Admin', 'Customer Service', 'Sales Rep'][Math.floor(Math.random() * 3)]
    }))
  }));
};

// Table utility functions
export const getComparator = (order, orderBy) => {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
};

export const descendingComparator = (a, b, orderBy) => {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
};

export const stableSort = (array, comparator) => {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
};

// Bulk operations
export const bulkUpdateStatus = async (orderIds, newStatus) => {
  try {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    /* 
    // Production API call would look like:
    const response = await fetch('/api/orders/bulk-update-status', {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ orderIds, status: newStatus })
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    */

   return { success: true, updatedCount: orderIds.length };
  } catch (error) {
    console.error('Error bulk updating orders:', error);
    return { success: false, error: error.message };
  }
};

export const bulkSendEmail = async (orderIds, emailTemplate) => {
  try {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    /* 
    // Production API call would look like:
    const response = await fetch('/api/orders/bulk-send-email', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ orderIds, emailTemplate })
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    */
    
    return { success: true, sentCount: orderIds.length };
  } catch (error) {
    console.error('Error bulk sending emails:', error);
    return { success: false, error: error.message };
  }
};

// Advanced filtering
export const getAdvancedFilters = () => {
  return {
    dateRanges: [
      { label: 'Today', value: 'today' },
      { label: 'Yesterday', value: 'yesterday' },
      { label: 'Last 7 days', value: 'last7days' },
      { label: 'Last 30 days', value: 'last30days' },
      { label: 'This month', value: 'thismonth' },
      { label: 'Last month', value: 'lastmonth' },
      { label: 'Custom range', value: 'custom' }
    ],
    amountRanges: [
      { label: 'Under $500', min: 0, max: 500 },
      { label: '$500 - $1,000', min: 500, max: 1000 },
      { label: '$1,000 - $5,000', min: 1000, max: 5000 },
      { label: '$5,000 - $10,000', min: 5000, max: 10000 },
      { label: 'Over $10,000', min: 10000, max: null }
    ],
    customerTypes: [
      { label: 'New Customer', value: 'new' },
      { label: 'Returning Customer', value: 'returning' },
      { label: 'VIP Customer', value: 'vip' }
    ]
  };
};

// Report generation
export const generateOrderReport = (orders, reportType = 'summary') => {
  const report = {
    generatedAt: new Date(),
    reportType,
    totalOrders: orders.length,
    data: {}
  };

  switch (reportType) {
    case 'summary':
      report.data = {
        statusBreakdown: calculateStatusBreakdown(orders),
        priorityBreakdown: calculatePriorityBreakdown(orders),
        revenueMetrics: calculateRevenueMetrics(orders),
        timeMetrics: calculateTimeMetrics(orders)
      };
      break;
    
    case 'detailed':
      report.data = {
        orders: orders.map(generateOrderSummary),
        metrics: calculateMetrics(orders)
      };
      break;
    
    case 'analytics':
      report.data = {
        trends: calculateTrends(orders),
        performance: calculatePerformance(orders),
        insights: generateInsights(orders)
      };
      break;
    
    default:
      report.data = { orders };
  }

  return report;
};

const calculateStatusBreakdown = (orders) => {
  const breakdown = {};
  orders.forEach(order => {
    breakdown[order.status] = (breakdown[order.status] || 0) + 1;
  });
  return breakdown;
};

const calculatePriorityBreakdown = (orders) => {
  const breakdown = {};
  orders.forEach(order => {
    breakdown[order.priority] = (breakdown[order.priority] || 0) + 1;
  });
  return breakdown;
};

const calculateRevenueMetrics = (orders) => {
  const totalRevenue = orders.reduce((sum, order) => sum + (order.totalAmount || 0), 0);
  const avgOrderValue = orders.length > 0 ? totalRevenue / orders.length : 0;
  
  return {
    totalRevenue,
    avgOrderValue,
    highestOrder: Math.max(...orders.map(o => o.totalAmount || 0)),
    lowestOrder: Math.min(...orders.map(o => o.totalAmount || 0))
  };
};

const calculateTimeMetrics = (orders) => {
  const now = new Date();
  const today = orders.filter(o => 
    new Date(o.orderDate).toDateString() === now.toDateString()
  ).length;
  
  const thisWeek = orders.filter(o => {
    const orderDate = new Date(o.orderDate);
    const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    return orderDate >= weekAgo;
  }).length;
  
  return { today, thisWeek };
};

const calculateTrends = (orders) => {
  // Calculate daily order trends for the last 30 days
  const trends = {};
  const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
  
  orders.forEach(order => {
    const orderDate = new Date(order.orderDate);
    if (orderDate >= thirtyDaysAgo) {
      const dateKey = orderDate.toDateString();
      trends[dateKey] = (trends[dateKey] || 0) + 1;
    }
  });
  
  return trends;
};

const calculatePerformance = (orders) => {
  const completedOrders = orders.filter(o => ['completed', 'delivered'].includes(o.status));
  const completionRate = orders.length > 0 ? (completedOrders.length / orders.length) * 100 : 0;
  
  const avgProcessingTime = calculateAvgProcessingTime(completedOrders);
  
  return {
    completionRate,
    avgProcessingTime,
    totalCompleted: completedOrders.length
  };
};

const calculateAvgProcessingTime = (completedOrders) => {
  if (completedOrders.length === 0) return 0;
  
  const totalTime = completedOrders.reduce((sum, order) => {
    const orderDate = new Date(order.orderDate);
    const completedDate = new Date(order.lastInteraction);
    return sum + (completedDate - orderDate);
  }, 0);
  
  return totalTime / completedOrders.length / (1000 * 60 * 60 * 24); // Convert to days
};

const generateInsights = (orders) => {
  const insights = [];
  
  // High priority orders insight
  const highPriorityCount = orders.filter(o => ['high', 'urgent'].includes(o.priority)).length;
  if (highPriorityCount > orders.length * 0.3) {
    insights.push({
      type: 'warning',
      message: `${highPriorityCount} high priority orders require immediate attention`
    });
  }
  
  // Pending orders insight
  const pendingCount = orders.filter(o => o.status === 'pending').length;
  if (pendingCount > orders.length * 0.2) {
    insights.push({
      type: 'info',
      message: `${pendingCount} orders are still pending and may need follow-up`
    });
  }
  
  // Revenue insight
  const metrics = calculateMetrics(orders);
  if (metrics.avgOrderValue > 5000) {
    insights.push({
      type: 'success',
      message: `High average order value of ${formatCurrency(metrics.avgOrderValue)} indicates strong sales performance`
    });
  }
  
  return insights;
};

// Export utilities
export const exportToExcel = (orders) => {
  // This would require a library like xlsx
  console.log('Excel export functionality would be implemented here');
  return { success: true, message: 'Excel export feature coming soon' };
};

export const exportToPDF = (orders) => {
  // This would require a library like jsPDF
  console.log('PDF export functionality would be implemented here');
  return { success: true, message: 'PDF export feature coming soon' };
};

// Notification utilities
export const shouldShowNotification = (order) => {
  const attention = orderNeedsAttention(order);
  return attention !== null;
};

export const getNotificationMessage = (order) => {
  const attention = orderNeedsAttention(order);
  if (!attention) return null;
  
  return {
    title: `Order ${order.id} Needs Attention`,
    message: attention.reason,
    severity: attention.level,
    orderId: order.id
  };
};

// Search utilities
export const getSearchSuggestions = (orders, searchTerm) => {
  if (!searchTerm || searchTerm.length < 2) return [];
  
  const suggestions = new Set();
  const searchLower = searchTerm.toLowerCase();
  
  orders.forEach(order => {
    if (order.id.toLowerCase().includes(searchLower)) {
      suggestions.add(order.id);
    }
    if (order.customerName.toLowerCase().includes(searchLower)) {
      suggestions.add(order.customerName);
    }
    if (order.supplierName.toLowerCase().includes(searchLower)) {
      suggestions.add(order.supplierName);
    }
  });
  
  return Array.from(suggestions).slice(0, 5);
};

// Keyboard shortcuts
export const getKeyboardShortcuts = () => {
  return {
    'Ctrl+R': 'Refresh orders',
    'Ctrl+F': 'Focus search',
    'Ctrl+E': 'Export orders',
    'Ctrl+A': 'Select all orders',
    'Escape': 'Clear selection',
    'Enter': 'Open selected order details'
  };
};

// Grid layout utilities for Tailwind CSS
export const getGridClasses = (columns = 1, gap = 4) => {
  const columnClasses = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
    5: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5',
    6: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6'
  };
  
  const gapClasses = {
    1: 'gap-1',
    2: 'gap-2',
    3: 'gap-3',
    4: 'gap-4',
    5: 'gap-5',
    6: 'gap-6',
    8: 'gap-8'
  };
  
  return `grid ${columnClasses[columns] || columnClasses[1]} ${gapClasses[gap] || gapClasses[4]}`;
};

// Flex utilities for Tailwind CSS
export const getFlexClasses = (direction = 'row', justify = 'start', align = 'start', wrap = false) => {
  const directionClasses = {
    row: 'flex-row',
    'row-reverse': 'flex-row-reverse',
    col: 'flex-col',
    'col-reverse': 'flex-col-reverse'
  };
  
  const justifyClasses = {
    start: 'justify-start',
    end: 'justify-end',
    center: 'justify-center',
    between: 'justify-between',
    around: 'justify-around',
    evenly: 'justify-evenly'
  };
  
  const alignClasses = {
    start: 'items-start',
    end: 'items-end',
    center: 'items-center',
    baseline: 'items-baseline',
    stretch: 'items-stretch'
  };
  
  const wrapClass = wrap ? 'flex-wrap' : 'flex-nowrap';
  
  return `flex ${directionClasses[direction]} ${justifyClasses[justify]} ${alignClasses[align]} ${wrapClass}`;
};

// Spacing utilities
export const getSpacingClasses = (type = 'padding', size = 4, sides = 'all') => {
  const prefix = type === 'padding' ? 'p' : 'm';
  
  const sideMap = {
    all: '',
    x: 'x',
    y: 'y',
    t: 't',
    r: 'r',
    b: 'b',
    l: 'l'
  };
  
  return `${prefix}${sideMap[sides]}-${size}`;
};

// Typography utilities
export const getTypographyClasses = (variant = 'body1') => {
  const variants = {
    h1: 'text-4xl font-bold leading-tight',
    h2: 'text-3xl font-bold leading-tight',
    h3: 'text-2xl font-semibold leading-tight',
    h4: 'text-xl font-semibold leading-tight',
    h5: 'text-lg font-medium leading-tight',
    h6: 'text-base font-medium leading-tight',
    subtitle1: 'text-base font-medium',
    subtitle2: 'text-sm font-medium',
    body1: 'text-base',
    body2: 'text-sm',
    caption: 'text-xs text-gray-600',
    overline: 'text-xs uppercase tracking-wide font-medium'
  };
  
  return variants[variant] || variants.body1;
};

// Shadow utilities
export const getShadowClasses = (elevation = 1) => {
  const shadows = {
    0: 'shadow-none',
    1: 'shadow-sm',
    2: 'shadow',
    3: 'shadow-md',
    4: 'shadow-lg',
    5: 'shadow-xl',
    6: 'shadow-2xl'
  };
  
  return shadows[elevation] || shadows[1];
};

// Border radius utilities
export const getBorderRadiusClasses = (size = 'md') => {
  const sizes = {
    none: 'rounded-none',
    sm: 'rounded-sm',
    md: 'rounded',
    lg: 'rounded-lg',
    xl: 'rounded-xl',
    '2xl': 'rounded-2xl',
    '3xl': 'rounded-3xl',
    full: 'rounded-full'
  };
  
  return sizes[size] || sizes.md;
};

// Animation utilities
export const getAnimationClasses = (type = 'fade') => {
  const animations = {
    fade: 'transition-opacity duration-300 ease-in-out',
    slide: 'transition-transform duration-300 ease-in-out',
    scale: 'transition-transform duration-200 ease-in-out hover:scale-105',
    bounce: 'animate-bounce',
    pulse: 'animate-pulse',
    spin: 'animate-spin',
    ping: 'animate-ping'
  };
  
  return animations[type] || animations.fade;
};

// Color utilities for dynamic theming
export const getColorClasses = (color = 'blue', variant = 'primary', intensity = 500) => {
  const colorMap = {
    primary: `text-${color}-${intensity} bg-${color}-50 border-${color}-200`,
    secondary: `text-gray-${intensity} bg-gray-50 border-gray-200`,
    success: `text-green-${intensity} bg-green-50 border-green-200`,
    warning: `text-amber-${intensity} bg-amber-50 border-amber-200`,
    error: `text-red-${intensity} bg-red-50 border-red-200`,
    info: `text-blue-${intensity} bg-blue-50 border-blue-200`
  };
  
  return colorMap[variant] || colorMap.primary;
};

// Responsive utilities
export const getResponsiveClasses = (baseClass, breakpoints = {}) => {
  let classes = baseClass;
  
  Object.entries(breakpoints).forEach(([breakpoint, value]) => {
    if (value) {
      classes += ` ${breakpoint}:${value}`;
    }
  });
  
  return classes;
};

// Form validation utilities
export const getValidationClasses = (isValid = true, isTouched = false) => {
  if (!isTouched) {
    return 'border-gray-300 focus:border-blue-500 focus:ring-blue-500';
  }
  
  return isValid 
    ? 'border-green-300 focus:border-green-500 focus:ring-green-500'
    : 'border-red-300 focus:border-red-500 focus:ring-red-500';
};

// Loading state utilities
export const getLoadingClasses = (isLoading = false) => {
  return isLoading 
    ? 'opacity-50 pointer-events-none cursor-not-allowed'
    : 'opacity-100 pointer-events-auto cursor-pointer';
};

// Hover effect utilities
export const getHoverClasses = (effect = 'lift') => {
  const effects = {
    lift: 'hover:shadow-lg hover:-translate-y-1 transition-all duration-200',
    glow: 'hover:shadow-xl hover:shadow-blue-500/25 transition-shadow duration-200',
    scale: 'hover:scale-105 transition-transform duration-200',
    fade: 'hover:opacity-80 transition-opacity duration-200',
    slide: 'hover:translate-x-1 transition-transform duration-200'
  };
  
  return effects[effect] || effects.lift;
};

// Focus utilities
export const getFocusClasses = (variant = 'default') => {
  const variants = {
    default: 'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2',
    inset: 'focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500',
    none: 'focus:outline-none',
    visible: 'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500'
  };
  
  return variants[variant] || variants.default;
};

// Truncate text utilities
export const getTruncateClasses = (lines = 1) => {
  if (lines === 1) {
    return 'truncate';
  }
  
  return `overflow-hidden text-ellipsis line-clamp-${lines}`;
};

// Position utilities
export const getPositionClasses = (position = 'relative', placement = {}) => {
  let classes = `${position}`;
  
  Object.entries(placement).forEach(([side, value]) => {
    if (value !== undefined) {
      classes += ` ${side}-${value}`;
    }
  });
  
  return classes;
};

// Z-index utilities
export const getZIndexClasses = (level = 'auto') => {
  const levels = {
    auto: 'z-auto',
    0: 'z-0',
    10: 'z-10',
    20: 'z-20',
    30: 'z-30',
    40: 'z-40',
    50: 'z-50',
    modal: 'z-50',
    dropdown: 'z-40',
    tooltip: 'z-30'
  };
  
  return levels[level] || levels.auto;
};

// Accessibility utilities
export const getA11yClasses = (options = {}) => {
  const { 
    screenReaderOnly = false, 
    focusable = true, 
    ariaHidden = false 
  } = options;
  
  let classes = '';
  
  if (screenReaderOnly) {
    classes += 'sr-only';
  }
  
  if (!focusable) {
    classes += ' pointer-events-none';
  }
  
  if (ariaHidden) {
    classes += ' aria-hidden';
  }
  
  return classes.trim();
};

// Print utilities
export const getPrintClasses = (visible = true) => {
  return visible ? 'print:block' : 'print:hidden';
};

// Dark mode utilities
export const getDarkModeClasses = (lightClass, darkClass) => {
  return `${lightClass} dark:${darkClass}`;
};

// Container utilities
export const getContainerClasses = (size = 'default', center = true) => {
  const sizes = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl',
    '4xl': 'max-w-4xl',
    '5xl': 'max-w-5xl',
    '6xl': 'max-w-6xl',
    '7xl': 'max-w-7xl',
    full: 'max-w-full',
    default: 'container'
  };
  
  const centerClass = center ? 'mx-auto' : '';
  
  return `${sizes[size] || sizes.default} ${centerClass}`.trim();
};

// Utility function to combine classes
export const combineClasses = (...classes) => {
  return classes.filter(Boolean).join(' ');
};

// Conditional classes utility
export const conditionalClasses = (condition, trueClasses, falseClasses = '') => {
  return condition ? trueClasses : falseClasses;
};

// Variant classes utility
export const getVariantClasses = (variants, selectedVariant, defaultVariant = 'default') => {
  return variants[selectedVariant] || variants[defaultVariant] || '';
};


